import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import * as LucideIcons from 'lucide-react';

const Resources = () => {
  const resources = [
    {
      title: 'Investor Pitch Deck',
      description: '10-slide interactive presentation covering market opportunity, business model, and $750K funding ask',
      icon: 'Presentation',
      link: '/pitch',
      color: 'from-orange-500 to-red-500'
    },
    {
      title: 'Funding Education Guide',
      description: 'Complete guide to startup funding stages, target investors, and fundraising strategy from pre-seed to Series A',
      icon: 'GraduationCap',
      link: '/funding-guide',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Hardware Bill of Materials',
      description: 'Detailed prototype specs, component sourcing, vendor list, and $10K-$14K build guide',
      icon: 'Cpu',
      link: '/hardware-bom',
      color: 'from-green-500 to-emerald-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-md flex items-center justify-center">
                <LucideIcons.Zap className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-foreground">YardEdge</span>
            </div>
            <Button 
              variant="outline" 
              onClick={() => window.location.href = '/'}
              className="border-gray-300"
            >
              <LucideIcons.Home className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 px-6 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6">
            Resources & Documentation
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Everything you need to understand YardEdge: investor materials, funding guides, and technical specifications
          </p>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {resources.map((resource, idx) => {
              const Icon = LucideIcons[resource.icon];
              return (
                <Card 
                  key={idx}
                  className="hover:shadow-xl transition-shadow cursor-pointer group"
                  onClick={() => window.location.href = resource.link}
                >
                  <CardHeader>
                    <div className={`w-16 h-16 bg-gradient-to-br ${resource.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <CardTitle className="text-2xl">{resource.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">{resource.description}</p>
                    <Button className="w-full bg-primary hover:bg-primary/90">
                      View Resource
                      <LucideIcons.ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-12">Quick Links</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Button 
              variant="outline" 
              className="h-auto py-4 flex items-center justify-start gap-4"
              onClick={() => window.location.href = '/#contact'}
            >
              <LucideIcons.Mail className="w-6 h-6 text-primary" />
              <div className="text-left">
                <div className="font-semibold">Contact Us</div>
                <div className="text-sm text-muted-foreground">Get in touch for partnerships</div>
              </div>
            </Button>
            
            <Button 
              variant="outline" 
              className="h-auto py-4 flex items-center justify-start gap-4"
              onClick={() => window.location.href = '/#pricing'}
            >
              <LucideIcons.DollarSign className="w-6 h-6 text-primary" />
              <div className="text-left">
                <div className="font-semibold">Pricing Plans</div>
                <div className="text-sm text-muted-foreground">View investment tiers</div>
              </div>
            </Button>
            
            <Button 
              variant="outline" 
              className="h-auto py-4 flex items-center justify-start gap-4"
              onClick={() => window.location.href = '/#how-it-works'}
            >
              <LucideIcons.Info className="w-6 h-6 text-primary" />
              <div className="text-left">
                <div className="font-semibold">How It Works</div>
                <div className="text-sm text-muted-foreground">Learn about our process</div>
              </div>
            </Button>
            
            <Button 
              variant="outline" 
              className="h-auto py-4 flex items-center justify-start gap-4"
              onClick={() => window.location.href = '/#faq'}
            >
              <LucideIcons.HelpCircle className="w-6 h-6 text-primary" />
              <div className="text-left">
                <div className="font-semibold">FAQ</div>
                <div className="text-sm text-muted-foreground">Common questions answered</div>
              </div>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-200 bg-gray-50">
        <div className="container mx-auto max-w-4xl text-center">
          <p className="text-muted-foreground">
            © 2025 YardEdge. All rights reserved. | <a href="https://www.theyardedge.com" className="text-primary hover:text-primary/80">TheYardEdge.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Resources;
